import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { Navigate, useParams } from "react-router";
import { getRuntimeConfig } from "@/app/config/runtime-config";
import { routePathById } from "@/app/router/route-manifest";
import { useAuth, type AuthStatus } from "@/core/auth/auth-provider";
import { useMemberInvoicesApi } from "./api/use-member-invoices-api";
import { MemberInvoicesApiError, type InvoiceLocatorResolution } from "./api/member-invoices";
import {
  InvoicesLoading,
  OPEN_INVOICES_PATH,
  RESOLVING_LABEL,
  TemporarilyUnavailable,
} from "./invoice-surface-states";
import { markLocatorEntry, scrubAddressBar } from "./locator-entry";
import { PayPublicChrome } from "./pay-public-chrome";

/**
 * 006-9 UI contract §2 (locator landing `/pay/i/:locator`) and §3.2
 * (post-login continuation `/pay/i?nav=<ref>`).
 *
 * The raw locator / navigation reference is read during the first render
 * (before any effect, request, or paint), the address bar is rewritten to the
 * bare `/pay/i` with `history.replaceState` in the layout phase of that same
 * commit (still before paint), and the value lives only in a component ref
 * until it has been POSTed. It never touches storage, other URLs, or the
 * router history (`<Navigate replace>` dispatch only).
 *
 * Capture is a pure read so `StrictMode`'s double invocation of the state
 * initializer sees the same URL twice; the scrub is idempotent so the effect
 * replay is harmless.
 */

export const PAY_GUIDANCE_PATH = routePathById("pay-guidance");
export const PAY_LANDING_PATH = routePathById("pay-locator-landing");
export const NAVIGATION_REFERENCE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** §2.2 `preselected` rows: the only `invoiceState` values that dispatch to `/pay/invoices/{invoiceId}`. */
const PRESELECTED_INVOICE_STATES: ReadonlySet<
  NonNullable<InvoiceLocatorResolution["invoiceState"]>
> = new Set(["payable", "paid", "not_payable"]);

type Entry =
  | { kind: "locator"; locator: string }
  | { kind: "continuation"; navigationReference: string };

type Dispatch =
  | { kind: "pending" }
  | { kind: "navigate"; to: string }
  | { kind: "token_failed"; error: MemberInvoicesApiError };

export interface PayLocatorLandingPageProps {
  /** Browser-level navigation for the login hand-off; defaults to `window.location.assign`. */
  navigateToLogin?: (url: string) => void;
}

function captureEntry(locatorParam: string | undefined): Entry | undefined {
  // §2.1: the path segment is captured raw and forwarded unchanged — the
  // client never validates locator shape (that's the server's contract).
  if (locatorParam !== undefined) {
    return { kind: "locator", locator: locatorParam };
  }
  // §3.2: only the LOCAL `nav` value is shape-checked; absent/empty/malformed
  // → open invoices with zero requests.
  const nav = new URLSearchParams(window.location.search).get("nav");
  if (nav !== null && NAVIGATION_REFERENCE_PATTERN.test(nav)) {
    return { kind: "continuation", navigationReference: nav };
  }
  return undefined;
}

function isSettled(status: AuthStatus): boolean {
  // "logout" is a sign-out in flight: the provider settles to
  // "unauthenticated" (or unloads the page) right after, so it is not a
  // snapshot to resolve against.
  return (
    status !== "loading" && status !== "refreshing" && status !== "returning" && status !== "logout"
  );
}

function buildLoginHandoff(navigationReference: string): string {
  const target = new URL("/auth/login", getRuntimeConfig().authBaseUrl);
  target.searchParams.set("returnTo", `${PAY_LANDING_PATH}?nav=${navigationReference}`);
  return target.toString();
}

function dispatchForResolution(
  resolution: InvoiceLocatorResolution,
  entry: Entry,
  navigateToLogin: (url: string) => void,
): Dispatch {
  // §2.2: `invoiceId`/`invoiceState` are read only for `preselected`, and only
  // the two listed combinations dispatch; anything else is `guidance`. The
  // target route re-reads the projection under its own authorization (§5.1).
  if (
    resolution.outcome === "preselected" &&
    typeof resolution.invoiceId === "string" &&
    resolution.invoiceId !== "" &&
    resolution.invoiceState != null &&
    PRESELECTED_INVOICE_STATES.has(resolution.invoiceState)
  ) {
    const invoiceId: string = resolution.invoiceId;
    // §5.2 / FR-018: the payment step reached from here prepares with `entry: locator`.
    markLocatorEntry(invoiceId);
    return { kind: "navigate", to: `${OPEN_INVOICES_PATH}/${encodeURIComponent(invoiceId)}` };
  }
  if (
    resolution.outcome === "login_required" &&
    entry.kind === "locator" &&
    typeof resolution.navigationReference === "string" &&
    NAVIGATION_REFERENCE_PATTERN.test(resolution.navigationReference)
  ) {
    navigateToLogin(buildLoginHandoff(resolution.navigationReference));
    // Browser navigation is in flight; keep the Resolving cue until unload.
    return { kind: "pending" };
  }
  // guidance, login_required from continue, invalid reference, or any other
  // combination → identical guidance surface.
  return { kind: "navigate", to: PAY_GUIDANCE_PATH };
}

export function PayLocatorLandingPage({
  navigateToLogin = (url) => window.location.assign(url),
}: PayLocatorLandingPageProps = {}): ReactElement {
  const { locator } = useParams<{ locator: string }>();
  const auth = useAuth();
  const api = useMemberInvoicesApi();

  // Render-time capture (pure), then the layout-phase scrub: both run before
  // any passive effect, lazy chunk, request, or paint.
  const entryRef = useRef<Entry | undefined>(undefined);
  const [dispatch, setDispatch] = useState<Dispatch>(() => {
    const entry = captureEntry(locator);
    entryRef.current = entry;
    return entry ? { kind: "pending" } : { kind: "navigate", to: OPEN_INVOICES_PATH };
  });
  useLayoutEffect(() => {
    scrubAddressBar(PAY_LANDING_PATH);
  }, []);

  // One-shot gate: fires on the first settled auth status, captures that
  // snapshot, and ignores the provider's own `refreshing` flip caused by our
  // token acquisition.
  const gateFiredRef = useRef(false);
  const inFlightRef = useRef(false);
  // A resolution that settles after the member left this route must not
  // dispatch anything — in particular no hard `navigateToLogin`. Reset on
  // mount so StrictMode's mount→cleanup→mount replay ends live.
  const disposedRef = useRef(false);
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);
  const navigateToLoginRef = useRef(navigateToLogin);
  navigateToLoginRef.current = navigateToLogin;

  const run = useCallback(
    async (snapshotAuthenticated: boolean): Promise<void> => {
      const entry = entryRef.current;
      if (!entry || inFlightRef.current) {
        return;
      }
      if (entry.kind === "continuation" && !snapshotAuthenticated) {
        // §0/§3.2: the continue POST carries the platform bearer of a signed-in
        // session; with a not-signed-in snapshot there is none to carry, so no
        // token acquisition from this public route (§2) and no POST — the
        // reference is dropped and the neutral guidance surface applies.
        entryRef.current = undefined;
        setDispatch({ kind: "navigate", to: PAY_GUIDANCE_PATH });
        return;
      }
      inFlightRef.current = true;
      try {
        let resolution: InvoiceLocatorResolution;
        if (entry.kind === "continuation") {
          resolution = await api.continueInvoiceLocator({
            navigationReference: entry.navigationReference,
          });
        } else if (!snapshotAuthenticated) {
          // §2: no session in the snapshot → the public resolve, with no token
          // acquisition at all (a public route never provokes a refresh).
          resolution = await api.resolveInvoiceLocator(
            { locator: entry.locator },
            { anonymous: true },
          );
        } else {
          try {
            resolution = await api.resolveInvoiceLocator(
              { locator: entry.locator },
              { anonymous: false },
            );
          } catch (error) {
            if (error instanceof MemberInvoicesApiError && error.kind === "token_unavailable") {
              // Authenticated snapshot but no bearer: never resolve
              // anonymously; offer retry with the locator still in memory.
              setDispatch({ kind: "token_failed", error });
              return;
            }
            throw error;
          }
        }
        // Drop the raw value as soon as it has been consumed.
        entryRef.current = undefined;
        if (disposedRef.current) {
          return;
        }
        setDispatch(dispatchForResolution(resolution, entry, navigateToLoginRef.current));
      } catch (error) {
        if (disposedRef.current) {
          entryRef.current = undefined;
          return;
        }
        if (
          error instanceof MemberInvoicesApiError &&
          error.kind === "token_unavailable" &&
          snapshotAuthenticated
        ) {
          setDispatch({ kind: "token_failed", error });
          return;
        }
        entryRef.current = undefined;
        setDispatch({ kind: "navigate", to: PAY_GUIDANCE_PATH });
      } finally {
        inFlightRef.current = false;
      }
    },
    [api],
  );

  useEffect(() => {
    if (gateFiredRef.current || !entryRef.current || !isSettled(auth.status)) {
      return;
    }
    gateFiredRef.current = true;
    void run(auth.status !== "unauthenticated");
  }, [auth.status, run]);

  const retry = useCallback(() => {
    setDispatch({ kind: "pending" });
    void run(true);
  }, [run]);

  if (dispatch.kind === "navigate") {
    return <Navigate to={dispatch.to} replace />;
  }
  if (dispatch.kind === "token_failed") {
    return (
      <PayPublicChrome>
        <TemporarilyUnavailable
          error={dispatch.error}
          onRetry={retry}
          supportAction
          presentation="public"
        />
      </PayPublicChrome>
    );
  }
  return (
    <PayPublicChrome>
      <InvoicesLoading label={RESOLVING_LABEL} />
    </PayPublicChrome>
  );
}
